import {
  ManageOwnershipDialog,
  ORIGINAL_REPOSITORY_NAME_ANNOTATION,
  entityToOwnershipEntity,
  getDisabledSubscriptionOwnershipTooltip,
  getSubscriptionTechnicalContactRefs,
  getSubscriptionTechnicalContactsForDisplay,
} from '@internal/backstage-plugin-common-library-react';
import AutoFixNormal from '@mui/icons-material/AutoFixNormal';
import React, { useCallback, useMemo, useState } from 'react';
import { useAsync } from 'react-use';

import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

import {
  Button,
  Typography,
  CircularProgress,
  Tooltip,
} from '@material-ui/core';

import { ResourceGroupTagFix } from '../components/fixes/ResourceGroupTagFix';
import { FixableCategory, FixButtonProps } from '../types';

const VelocityFix: React.FC<FixButtonProps> = () => {
  return null;
};

const EndOfLifeFix: React.FC<FixButtonProps> = () => {
  return null;
};

const PrivilegedIdentityManagementFix: React.FC<FixButtonProps> = () => {
  return null;
};

const KeyRotationFix: React.FC<FixButtonProps> = () => {
  return null;
};

const AgentCoverageFix: React.FC<FixButtonProps> = () => {
  return null;
};

const PublicIpFix: React.FC<FixButtonProps> = () => {
  return null;
};

const CRITICALITY_SET_URL =
  'https://github.com/tomtom-internal/meta-data/issues/new?template=security_criticality_issue.yml';
const DEFAULT_GH_ORG = 'https://github.com/tomtom-internal/';

const CriticalitySetFix: React.FC<FixButtonProps> = ({ context }) => {
  const fullURL = useMemo(() => {
    return `${CRITICALITY_SET_URL}&title=[Security-critical repository]: ${context.repository_name}&repository=${DEFAULT_GH_ORG}${context.repository_name}`;
  }, [context.repository_name]);

  const handleClick = () => {
    window.open(fullURL, '_blank', 'noopener,noreferrer');
  };
  return (
    <>
      <Button
        variant="contained"
        color="primary"
        size="small"
        startIcon={<AutoFixNormal />}
        onClick={handleClick}
      >
        Fix
      </Button>
      <Typography
        variant="caption"
        display="block"
        style={{ fontSize: '0.7rem', marginTop: 4 }}
      >
        This is an external action, may take some time to fix
      </Typography>
    </>
  );
};

const BlackduckCoverageFix: React.FC<FixButtonProps> = ({ context }) => {
  const handleClick = () => {
    const repositoryRef = `repository:default/${context.repository_name}`;

    const params = new URLSearchParams({
      formData: JSON.stringify({
        repository: repositoryRef,
      }),
    });

    window.open(
      `/create/templates/default/blackduck-onboarding?${params.toString()}`,
      '_blank',
      'noopener,noreferrer',
    );
  };

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        size="small"
        startIcon={<AutoFixNormal />}
        onClick={handleClick}
      >
        Onboard to Blackduck
      </Button>
    </>
  );
};

const VeracodeCoverageFix: React.FC<FixButtonProps> = () => {
  return null;
};

const RepositoryOwnershipFix: React.FC<FixButtonProps> = ({
  context,
  performDataRefresh,
}) => {
  const catalogApi = useApi(catalogApiRef);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Fetch the repository entity when dialog opens
  const { loading, value: repoEntity } = useAsync(async () => {
    if (!dialogOpen || !context?.repository_name) return null;

    // Match the real repository name against either the entity name (normal
    // repos) or the original-name annotation (edge-case repos whose name was
    // sanitized during ingestion). An array of filters is OR-ed by the catalog.
    const response = await catalogApi.getEntities({
      filter: [
        { kind: 'Repository', 'metadata.name': context.repository_name },
        {
          kind: 'Repository',
          [`metadata.annotations.${ORIGINAL_REPOSITORY_NAME_ANNOTATION}`]:
            context.repository_name,
        },
      ],
    });

    return response.items[0] || null;
  }, [dialogOpen, context?.repository_name]);

  const handleClick = () => {
    setDialogOpen(true);
  };

  const handleClose = useCallback(() => {
    setDialogOpen(false);
  }, []);

  const handleSuccess = useCallback(() => {
    setDialogOpen(false);
    performDataRefresh?.();
  }, [performDataRefresh]);

  const ownershipEntities = useMemo(() => {
    if (!repoEntity) return [];
    return [entityToOwnershipEntity(repoEntity)];
  }, [repoEntity]);

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        size="small"
        startIcon={
          loading ? (
            <CircularProgress size={16} color="inherit" />
          ) : (
            <AutoFixNormal />
          )
        }
        onClick={handleClick}
        disabled={loading}
      >
        Fix Ownership
      </Button>
      {dialogOpen && !loading && ownershipEntities.length > 0 && (
        <ManageOwnershipDialog
          open={dialogOpen}
          entities={ownershipEntities}
          onClose={handleClose}
          onSuccess={handleSuccess}
          title="Fix Repository Ownership"
        />
      )}
    </>
  );
};

const SubscriptionOwnershipFix: React.FC<FixButtonProps> = ({
  context,
  performDataRefresh,
}) => {
  const catalogApi = useApi(catalogApiRef);
  const identityApi = useApi(identityApiRef);
  const [dialogOpen, setDialogOpen] = useState(false);

  const subscriptionId =
    context?.subscription_id ??
    context?.subscriptionId ??
    context?.id ??
    context?.resource_id;

  const subscriptionName =
    context?.subscription_name ?? context?.subscriptionName ?? context?.name;

  const { value: userEntityRef } = useAsync(async () => {
    const identity = await identityApi.getBackstageIdentity();
    return identity.userEntityRef.toLocaleLowerCase();
  }, [identityApi]);

  const { loading, value: subscriptionEntity } = useAsync(async () => {
    if (subscriptionId) {
      const response = await catalogApi.getEntities({
        filter: {
          kind: 'Resource',
          'metadata.name': `subscription-${subscriptionId}`,
        },
      });

      if (response.items[0]) {
        return response.items[0];
      }
    }

    if (subscriptionName) {
      const response = await catalogApi.getEntities({
        filter: {
          kind: 'Resource',
          'spec.type': 'subscription',
          'metadata.title': subscriptionName,
        },
      });

      return response.items[0] || null;
    }

    return null;
  }, [subscriptionId, subscriptionName, catalogApi]);

  const handleClick = () => {
    setDialogOpen(true);
  };

  const handleClose = useCallback(() => {
    setDialogOpen(false);
  }, []);

  const handleSuccess = useCallback(() => {
    setDialogOpen(false);
    performDataRefresh?.();
  }, [performDataRefresh]);

  const ownershipEntities = useMemo(() => {
    if (!subscriptionEntity) return [];
    return [entityToOwnershipEntity(subscriptionEntity)];
  }, [subscriptionEntity]);

  const technicalContactRefs = useMemo(
    () => getSubscriptionTechnicalContactRefs(subscriptionEntity ?? undefined),
    [subscriptionEntity],
  );
  const technicalContactsDisplay = useMemo(
    () =>
      getSubscriptionTechnicalContactsForDisplay(
        subscriptionEntity ?? undefined,
        technicalContactRefs,
      ),
    [subscriptionEntity, technicalContactRefs],
  );
  const isTechnicalContact = Boolean(
    userEntityRef && technicalContactRefs.includes(userEntityRef),
  );
  const buttonDisabled = loading || !isTechnicalContact;

  let tooltipTitle: React.ReactNode = '';
  if (!loading && !isTechnicalContact) {
    tooltipTitle = getDisabledSubscriptionOwnershipTooltip({
      technicalContactsDisplay,
    });
  }

  return (
    <>
      <Tooltip title={tooltipTitle ?? ''}>
        <span>
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={
              loading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <AutoFixNormal />
              )
            }
            onClick={handleClick}
            disabled={buttonDisabled}
          >
            Fix Ownership
          </Button>
        </span>
      </Tooltip>
      {dialogOpen && !loading && ownershipEntities.length > 0 && (
        <ManageOwnershipDialog
          open={dialogOpen}
          entities={ownershipEntities}
          onClose={handleClose}
          onSuccess={handleSuccess}
          title="Fix Subscription Ownership"
        />
      )}
    </>
  );
};

export const getFixForCategory = (
  category: FixableCategory,
): React.ComponentType<FixButtonProps> | null => {
  switch (category) {
    case 'Velocity':
      return VelocityFix;
    case 'End of Life':
      return EndOfLifeFix;
    case 'Privileged Identity Management':
      return PrivilegedIdentityManagementFix;
    case 'Key Rotation':
      return KeyRotationFix;
    case 'Agent Coverage':
      return AgentCoverageFix;
    case 'Public IP':
      return PublicIpFix;
    case 'Criticality Set':
      return CriticalitySetFix;
    case 'Blackduck Coverage':
      return BlackduckCoverageFix;
    case 'Veracode Coverage':
      return VeracodeCoverageFix;
    case 'Repository Ownership':
      return RepositoryOwnershipFix;
    case 'Subscription Ownership':
      return SubscriptionOwnershipFix;
    case 'Resource Group Tag':
      return ResourceGroupTagFix;
    default:
      return null;
  }
};
